// frontend/src/utils/tokenManager.js
import { SecureStorage } from './secureStorage';
// ✅ نسخة محسنة وموحدة - تدعم جميع أنواع المستخدمين

import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import config from '../config';
import { logger } from '../utils/logger';
class TokenManager {
  constructor() {
    this.refreshTimeout = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.refreshCallbacks = [];
    this.initialized = false;
  }

  /**
   * ✅ تهيئة التوكن عند بدء التطبيق
   */
  initialize() {
    if (this.initialized) return;

    const userRole = localStorage.getItem('userRole');
    if (!userRole) return;

    const token = this.getStoredToken(userRole);
    if (token && this.isTokenValid(token)) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      this.scheduleTokenRefresh(token, userRole);
      logger.log(`✅ Token initialized for role: ${userRole}`);
    } else if (token) {
      logger.warn('⚠️ Invalid or expired token found. Clearing...');
      this.clearAllData(userRole);
    }

    this.initialized = true;
  }

  /**
   * ✅ تحديد نوع المستخدم تلقائياً من التوكن
   */
  detectUserRoleFromToken(token) {
    try {
      const decoded = jwtDecode(token);
      if (decoded.role) {
        return decoded.role === 'agent' ? 'agent' : 'admin';
      }
      return 'client';
    } catch (error) {
      logger.error('❌ خطأ في تحديد نوع المستخدم:', error);
      return null;
    }
  }

  /**
   * ✅ الحصول على التوكن من المكان الصحيح حسب نوع المستخدم
   */
  getStoredToken(userRole = null) {
    if (!userRole) {
      userRole = localStorage.getItem('userRole');
    }

    if (!userRole) return null;

    // للعملاء: استخدم sessionStorage فقط
    if (userRole === 'client') {
      return SecureStorage.getItem('access_token', sessionStorage);
    }

    // للأدمن والوكلاء: استخدم localStorage
    const tokenKey = userRole === 'agent' ? 'agentToken' : 'adminToken';
    return localStorage.getItem(tokenKey);
  }

  /**
   * ✅ حفظ التوكن في المكان الصحيح
   */
  saveToken(token, userRole) {
    if (!userRole) {
      userRole = this.detectUserRoleFromToken(token);
    }

    if (!token || !userRole) {
      logger.error('❌ Cannot save token: missing token or userRole');
      return;
    }

    // حفظ userRole في localStorage (للتحقق فقط)
    localStorage.setItem('userRole', userRole);

    // للعملاء: sessionStorage فقط
    if (userRole === 'client') {
      SecureStorage.setItem('access_token', token, sessionStorage);
    } else {
      // للأدمن والوكلاء: localStorage
      const tokenKey = userRole === 'agent' ? 'agentToken' : 'adminToken';
      localStorage.setItem(tokenKey, token);
    }

    // تحديث axios headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    logger.log(`✅ Token saved for ${userRole}`);
  }

  /**
   * حساب الوقت المتبقي قبل انتهاء صلاحية التوكن (بالمللي ثانية)
   */
  getTokenExpirationTime(token) {
    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return null;

      const expirationTime = decoded.exp * 1000;
      const now = Date.now();
      return expirationTime - now;
    } catch (error) {
      logger.error('❌ خطأ في فك تشفير التوكن:', error);
      return null;
    }
  }

  /**
   * جدولة تحديث التوكن قبل انتهاء صلاحيته بـ 5 دقائق
   */
  scheduleTokenRefresh(token, userRole = null) {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    if (!userRole) {
      userRole = this.detectUserRoleFromToken(token) || localStorage.getItem('userRole');
    }

    const expirationTime = this.getTokenExpirationTime(token);

    if (!expirationTime || expirationTime < 0) {
      logger.warn('⚠️ التوكن منتهي الصلاحية بالفعل');
      this.logout(userRole);
      return;
    }

    // ✅ التحديث قبل 5 دقائق (300000 مللي ثانية)
    const refreshTime = Math.max(expirationTime - 5 * 60 * 1000, 30000);

    logger.log(
      `⏱️ [${userRole}] جدولة تحديث التوكن في ${Math.floor(refreshTime / 1000)} ثانية ` +
      `(${Math.floor(refreshTime / 60000)} دقيقة)`
    );

    this.refreshTimeout = setTimeout(() => {
      logger.log(`🔄 [${userRole}] بدء تحديث التوكن...`);
      this.refreshToken(userRole);
    }, refreshTime);
  }

  /**
   * تحديث التوكن من الخادم
   */
  async refreshToken(userRole = null) {
    if (!userRole) {
      userRole = localStorage.getItem('userRole') || 'client';
    }

    if (this.isRefreshing) {
      logger.log(`⏳ [${userRole}] جاري تحديث التوكن بالفعل...`);
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      try {
        const endpoint = userRole === 'admin'
          ? '/api/v1/admin/refresh-token'
          : userRole === 'agent'
            ? '/api/v1/agent/refresh-token'
            : '/api/v1/auth/refresh-token';

        const oldToken = this.getStoredToken(userRole);

        if (!oldToken) {
          throw new Error('لا يوجد توكن سابق لتحديثه');
        }

        logger.log(`🔄 [${userRole}] إرسال طلب تحديث التوكن...`);

        const response = await axios.post(
          `${config.API_BASE_URL}${endpoint}`,
          {},
          {
            headers: { 'Authorization': `Bearer ${oldToken}` },
            timeout: 10000,
            _skipInterceptor: true // ✅ منع الـ interceptor من إعادة المحاولة
          }
        );

        if (response.data.access_token) {
          const newToken = response.data.access_token;

          // حفظ التوكن الجديد
          this.saveToken(newToken, userRole);

          // جدولة التحديث القادم
          this.scheduleTokenRefresh(newToken, userRole);

          logger.log(`✅ [${userRole}] تم تحديث التوكن بنجاح`);

          // استدعاء callbacks
          this.refreshCallbacks.forEach(cb => cb(newToken, userRole));

          this.isRefreshing = false;
          return newToken;
        } else {
          throw new Error('لم يتم استلام توكن جديد');
        }
      } catch (error) {
        logger.error(`❌ [${userRole}] فشل تحديث التوكن:`, error.message);

        // لا تسجل الخروج فوراً إذا كان خطأ شبكة
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          logger.warn('⚠️ Refresh timeout, will retry on next request');
        } else {
          this.logout(userRole);
        }

        this.isRefreshing = false;
        throw error;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * ✅ مسح كل البيانات
   */
  clearAllData(userRole) {
    logger.log(`🧹 Clearing all data for ${userRole}...`);

    if (userRole === 'client') {
      sessionStorage.clear();
      localStorage.removeItem('clientData');
      localStorage.removeItem('clientData_persistence');
      localStorage.removeItem('client_data');
    } else {
      const tokenKey = userRole === 'agent' ? 'agentToken' : 'adminToken';
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(`${userRole}Permissions`);
    }

    localStorage.removeItem('userRole');
    delete axios.defaults.headers.common['Authorization'];

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  /**
   * ✅ تسجيل الخروج
   */
  async logout(userRole = null, skipServerCall = false) {
    if (!userRole) {
      userRole = localStorage.getItem('userRole') || 'client';
    }

    logger.log(`🚪 [${userRole}] Logging out...`);

    // إرسال طلب للخادم لإلغاء الجلسة
    if (!skipServerCall) {
      try {
        const token = this.getStoredToken(userRole);
        if (token) {
          await axios.post(
            `${config.API_BASE_URL}/api/v1/auth/logout`,
            {},
            {
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 5000
            }
          );
          logger.log(`✅ [${userRole}] Server session cleared`);
        }
      } catch (error) {
        logger.warn('⚠️ Logout request failed, clearing local data anyway:', error.message);
      }
    }

    // مسح البيانات المحلية
    this.clearAllData(userRole);

    // إعادة التوجيه
    const loginPath = userRole === 'agent'
      ? '/agent/login'
      : userRole === 'admin'
        ? '/admin/login'
        : '/login';

    logger.log(`📍 [${userRole}] Redirecting to ${loginPath}`);

    setTimeout(() => {
      window.location.href = loginPath;
    }, 100);
  }

  /**
   * التحقق من صلاحية التوكن
   */
  isTokenValid(token) {
    if (!token) return false;

    const expirationTime = this.getTokenExpirationTime(token);
    return expirationTime && expirationTime > 60000; // أكثر من دقيقة
  }

  /**
   * تسجيل callback
   */
  onTokenRefreshed(callback) {
    this.refreshCallbacks.push(callback);
  }

  /**
   * الحصول على معلومات التوكن
   */
  getTokenInfo(token) {
    try {
      const decoded = jwtDecode(token);
      const expirationTime = this.getTokenExpirationTime(token);

      return {
        userId: decoded.sub,
        role: decoded.role || 'client',
        permissions: decoded.permissions || [],
        expiresIn: Math.floor(expirationTime / 1000),
        isExpired: expirationTime <= 0
      };
    } catch (error) {
      logger.error('❌ خطأ في الحصول على معلومات التوكن:', error);
      return null;
    }
  }
}

const tokenManager = new TokenManager();
export default tokenManager;